import React from 'react';
import {
  useHomeMoveStatusQuery,
  useHomeMoveCustomerQuery,
} from '@/src/api/home-moves';
import { useIsTuwyCustomerQuery } from '@/src/api/use-is-tuwy-customer';
import ErrorSection from '@/src/components/ErrorSection/ErrorSection';
import { SkeletonCard } from '@/src/pages/Home/components/SkeletonCard';
import { Card, Heading3 } from '@ovotech/element-v4';
import {
  AnalyticsPage,
  useAccountContext,
  useCustomerContext,
} from '@ovotech/ui-tools';
import { MovingHomeConfirmWithData } from './components/MovingHomeConfirm/MovingHomeConfirm';
import { MovingHomeForm } from './components/MovingHomeForm/MovingHomeForm';
import { ContractTerminatedSection } from '@/src/pages/MovingHome/components/ContractTerminatedSection/ContractTerminatedSection';
import { MoveOutSupplyingHomeScreen } from './components/MoveOutSupplyingHomeScreen/MoveOutSupplyingHomeScreen';
import { isBefore, startOfDay } from 'date-fns';
import { formatMoveOutAddress } from './utils/formatMoveOutAddress';
import { formatPostalAddress } from '@/src/utils/format-postal-address';
import { useSupplyPointsQuery } from '@/src/api/kapi/use-supply-points-query';
import { HOME_MOVES_MOVING_HOME } from '@/src/constants/analytics-subjects';
import { useRewardsFeature } from './components/Rewards';
import { formatSupplyPointAddress } from '@/src/api/kapi/utils/mapMoveOutQueryCustomerData';
import { useEnergyQuoteEligibility } from '@/src/api/use-energy-quote-eligibility';
import { calculateIsCotCustomer } from './utils/calculateIsCotCustomer';
import { getProviderAddressFromCustomerData } from './utils/getProviderAddressFromCustomerData';

/**
 * MovingHomePage component.
 * Displays either a loading state, an error state, the MoveOutSupplyingHomeScreen component if customer is staying
 * with OVO and move date has passed, the MovingHomeConfirmWithData component if a move out date
 * is present, or the MovingHomeForm component if no move out date is present.
 */
export const MovingHomePage = () => {
  const { accountId } = useAccountContext();
  const { customerId } = useCustomerContext();

  const moveOutStatusQuery = useHomeMoveStatusQuery(accountId);
  const moveOutCustomerQuery = useHomeMoveCustomerQuery(accountId);
  const takingOvoWithThemQuery = useIsTuwyCustomerQuery(customerId);

  const moveOutProviderAddress = moveOutCustomerQuery.data
    ? getProviderAddressFromCustomerData(moveOutCustomerQuery.data)
    : null;

  const eligibilityQuote = useEnergyQuoteEligibility(
    {
      postCode: moveOutProviderAddress?.postcode ?? '',
      propertySearchId: moveOutProviderAddress?.propertySearchId ?? '',
    },
    {
      keepPreviousData: true,
      refetchOnWindowFocus: false,
      refetchOnMount: false,
      cacheTime: 0,
    },
  );

  const supplyPointsQuery = useSupplyPointsQuery();
  const accountSupplyPoints =
    supplyPointsQuery?.data?.account?.accountSupplyPoints;

  const address =
    accountSupplyPoints &&
    accountSupplyPoints.length > 0 &&
    accountSupplyPoints[0].supplyPoint?.address &&
    formatPostalAddress(accountSupplyPoints[0].supplyPoint?.address);

  const moveOutDateString = moveOutStatusQuery.data?.moveOutDate;
  const moveOutDate = moveOutDateString && new Date(moveOutDateString);
  const isTakingOvo = !!takingOvoWithThemQuery.data?.isTuwyCustomer;
  const isMoveDatePassed =
    moveOutDate && isBefore(moveOutDate, startOfDay(new Date()));
  const customerData = moveOutCustomerQuery.data;

  const gasMeterInformation = eligibilityQuote?.data?.gasMeterInformation ?? [];
  const electricityMeterInformation =
    eligibilityQuote?.data?.electricityMeterInformation ?? [];
  const requestedFuel = eligibilityQuote?.data?.requestedFuel;

  const newAddressIsCot = calculateIsCotCustomer({
    requestedFuel,
    gasMeterInformation,
    electricityMeterInformation,
  });

  const { trackingData: rewardsTrackingData } = useRewardsFeature();

  if (
    moveOutStatusQuery.isLoading ||
    moveOutCustomerQuery.isLoading ||
    takingOvoWithThemQuery.isLoading ||
    supplyPointsQuery.isLoading ||
    eligibilityQuote.isLoading
  ) {
    return <SkeletonCard />;
  }

  if (moveOutStatusQuery.isError) {
    return (
      <AnalyticsPage
        name={HOME_MOVES_MOVING_HOME}
        properties={{
          isKaluzaEvent: false,
          isOVOTUWYJourney: true,
          moveOutStatus: 'noHomeMove',
        }}
      >
        <Card>
          <ErrorSection
            headingComponent={Heading3}
            id={'move-out-data-error'}
          />
        </Card>
      </AnalyticsPage>
    );
  }

  // If no move-out date show the form
  if (
    !moveOutStatusQuery.data ||
    !moveOutDateString ||
    (moveOutStatusQuery.data.canSelfServe && !moveOutDateString)
  ) {
    return (
      <AnalyticsPage
        name={HOME_MOVES_MOVING_HOME}
        properties={{
          isKaluzaEvent: false,
          isOVOTUWYJourney: true,
          moveOutStatus: 'error',
          ...(rewardsTrackingData && {
            optimizelyExperiments: {
              ...rewardsTrackingData,
            },
          }),
        }}
      >
        <MovingHomeForm />
      </AnalyticsPage>
    );
  }

  // Format the move-out address
  const moveOutAddress =
    (customerData
      ? formatMoveOutAddress(customerData)
      : moveOutStatusQuery.data.supplyPointAddress
      ? formatSupplyPointAddress(moveOutStatusQuery.data.supplyPointAddress)
      : '') ?? '';

  if (isMoveDatePassed) {
    return isTakingOvo ? (
      <AnalyticsPage
        name={HOME_MOVES_MOVING_HOME}
        properties={{
          isKaluzaEvent: false,
          isOVOTUWYJourney: true,
          moveOutStatus: 'start',
        }}
      >
        <MoveOutSupplyingHomeScreen
          moveOutAddress={moveOutAddress}
          moveOutDate={moveOutDateString}
        />
      </AnalyticsPage>
    ) : (
      <AnalyticsPage
        name={HOME_MOVES_MOVING_HOME}
        properties={{
          isKaluzaEvent: false,
          isOVOTUWYJourney: true,
          isTakingOvo,
          moveOutStatus: 'complete',
        }}
      >
        <ContractTerminatedSection
          currentAddress={address ? address : 'unknown'}
          moveOutCustomer={customerData ?? undefined}
          moveOutDate={moveOutDateString}
        />
      </AnalyticsPage>
    );
  }

  return (
    <AnalyticsPage
      name={HOME_MOVES_MOVING_HOME}
      properties={{
        isKaluzaEvent: false,
        isOVOTUWYJourney: true,
        isTakingOvo,
        moveOutStatus: 'inProgress',
      }}
    >
      <MovingHomeConfirmWithData
        moveOutCustomer={customerData ?? undefined}
        moveOutDate={moveOutDateString}
        isTakingOvo={isTakingOvo}
        newAddressIsCot={newAddressIsCot}
      />
    </AnalyticsPage>
  );
};
